import { createContext, useContext, useState, ReactNode, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { mainNavImages } from '@/data/pageImages';
import { isOverlayRoute, isSwipeSubRoute, resolveSwipeKey, stripHashAndQuery } from '@/lib/navigationRoutes';

interface PageConfig {
  image?: string;
  color: string;
  title: string;
}

type StoredLocation = {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
};

interface NavigationContextType {
  currentPage: PageConfig;
  hoveredPage: PageConfig | null;
  setHoveredPage: (page: PageConfig | null) => void;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  transitioning: boolean;
  setTransitioning: (transitioning: boolean) => void;
  pageConfigs: Record<string, PageConfig>;
  navigateToPage: (href: string) => void;
  lastBaseLocation: StoredLocation | null;
}

type NavigationState = {
  backgroundLocation?: StoredLocation | Location;
};

interface NavigationProviderProps {
  children: ReactNode;
  pathname: string;
  location?: Location | null;
}

const pageConfigs: Record<string, PageConfig> = {
  '/': { 
    title: 'Home',
    image: '/navigation/landing.png',
    color: '#F92E5F'
  },
  '/about': { 
    title: 'About',
    image: mainNavImages['about']?.image,
    color: '#41142A'
  },
  '/mach': {
    title: 'Mach',
    image: mainNavImages['mach']?.image,
    color: '#F92E5F'
  },
  '/nexus': {
    title: 'Nexus',
    image: mainNavImages['nexus']?.image,
    color: '#41142A'
  },
  '/work': {
    title: 'Work',
    image: mainNavImages['work']?.image,
    color: '#160C19'
  },
  '/services': { 
    title: 'Services',
    image: mainNavImages['services']?.image,
    color: '#FDD452'
  },
  '/contact': { 
    title: 'Contact',
    image: mainNavImages['contact']?.image,
    color: '#FCAD55'
  },
  '/blog': { 
    title: 'Blog',
    image: mainNavImages['blog']?.image,
    color: '#160C19'
  },
  '/insights': {
    title: 'Insights',
    image: mainNavImages['insights']?.image,
    color: '#41142A'
  },
  '/methodology': {
    title: 'Methodology',
    image: mainNavImages['methodology']?.image,
    color: '#FDD452'
  },
  '/demos': {
    title: 'Demos',
    image: mainNavImages['demos']?.image,
    color: '#F92E5F'
  },
  '/key-services/replicant': { 
    title: 'Replicant',
    image: '/videos/replicant-hero.mp4',
    color: '#F92E5F'
  },
};

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: FC<NavigationProviderProps> = ({ 
  children,
  pathname,
  location,
}) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [hoveredPage, setHoveredPage] = useState<PageConfig | null>(null);
  const deriveSwipeBase = (path?: string): StoredLocation | null => {
    if (!path) return null;
    if (!isSwipeSubRoute(path)) return null;
    const swipeBase = resolveSwipeKey(path);
    if (swipeBase && swipeBase !== '/') {
      return { pathname: swipeBase };
    }
    return null;
  };

  const initialBaseLocation: StoredLocation | null = (() => {
    if (!location) return null;
    if (isOverlayRoute(location.pathname)) {
      return deriveSwipeBase(location.pathname);
    }
    return {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    };
  })();

  const [lastBaseLocation, setLastBaseLocation] = useState<StoredLocation | null>(initialBaseLocation);
  
  const currentPage = pageConfigs[pathname] || { color: '#F92E5F', title: 'Page' };

  const toStoredLocation = (input?: StoredLocation | Location | null): StoredLocation | null => {
    if (!input) return null;
    const swipeBase = deriveSwipeBase(input.pathname);
    if (swipeBase) return swipeBase;
    return {
      pathname: input.pathname,
      search: input.search,
      hash: input.hash,
      state: (input as any).state,
    };
  };

  const getBaseLocation = () => {
    if (location) {
      const state = (location.state as NavigationState | undefined) ?? undefined;
      const base = (state?.backgroundLocation as StoredLocation | Location | undefined) ?? location;
      const stored = toStoredLocation(base);
      if (stored) {
        return stored;
      }
    }

    return lastBaseLocation ?? deriveSwipeBase(pathname) ?? undefined;
  };

  const navigateToPage = (href: string) => {
    if (href === pathname) return;
    const baseLocation = getBaseLocation();
    const targetPath = stripHashAndQuery(href);

    if (baseLocation && isOverlayRoute(targetPath)) {
      navigate(href, {
        state: { backgroundLocation: baseLocation },
      });
    } else {
      navigate(href);
    }
    setMenuOpen(false);
    setTransitioning(false);
    setHoveredPage(null);
  };

  useEffect(() => {
    if (!location) return;
    const currentPath = location.pathname;

    if (isOverlayRoute(currentPath)) {
      if (isSwipeSubRoute(currentPath)) {
        setLastBaseLocation((prev) => prev ?? deriveSwipeBase(currentPath));
      }
      return;
    }

    setLastBaseLocation({
      pathname: currentPath,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
  }, [location?.pathname, location?.search, location?.hash, location?.state]);

  // Simple SPA link interception (no animations)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if ((e as any).defaultPrevented) return;
      const target = e.target as HTMLElement;
      const link = target?.closest?.('a');
      if (link && (link as HTMLAnchorElement).href && !(link as HTMLAnchorElement).target && !(link as HTMLAnchorElement).download) {
        const url = new URL((link as HTMLAnchorElement).href);
        if (url.hostname === window.location.hostname) {
          e.preventDefault();
          navigateToPage(url.pathname + url.search + url.hash);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [pathname, location]);

  return (
    <NavigationContext.Provider 
      value={{
        currentPage,
        hoveredPage,
        setHoveredPage,
        menuOpen,
        setMenuOpen,
        transitioning,
        setTransitioning,
        pageConfigs,
        navigateToPage,
        lastBaseLocation,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};
